import { SectionUserActivityType } from "../../shared/types/attendance";

import {
  createAppSelector,
  createShallowRecordSelector,
  createStringArraySelector,
  selectCurrentSectionId,
  userSelectors,
} from "./base";
import { selectSectionUserIdsForPage } from "./page-groups";
import {
  selectStudentSectionUserIdsInCurrentSection,
  selectUserIdsWhoCanParticipateInSection,
} from "./permissions";
import {
  selectEnabledSectionUserIdsForCurrentSection,
  selectEnabledUserIdBySectionUserIdMap,
} from "./section-user";
import type { UserWithSectionActivity } from "./types";
import { SectionUserActivityStatus } from "./types";

import { RootState } from "store";
import { filterMap } from "utils/collections";

export const determineActivityStatus = (
  activity: SectionUserActivityType | null,
): SectionUserActivityStatus => {
  if (!activity) {
    return SectionUserActivityStatus.OFFLINE;
  }

  return activity.active === "0"
    ? SectionUserActivityStatus.INACTIVE
    : SectionUserActivityStatus.ACTIVE;
};

const determineCurrentPageId = (activity: SectionUserActivityType | null): string | null => {
  if (!activity) {
    return null;
  }

  return activity.pageId;
};

export const selectActivityStatus = (state: RootState) => state.interim.sectionUserActivities;

/** User ids that are active or inactive (not offline). Membership; order not meaningful. */
export const selectOnlineUserIds = createStringArraySelector(
  [selectActivityStatus],
  (sectionUserActivities) =>
    filterMap(
      Object.entries(sectionUserActivities),
      ([, activity]) => determineActivityStatus(activity) !== SectionUserActivityStatus.OFFLINE,
      ([userId]) => userId,
    ),
);

/** section_user_id → activity status for enabled section users. */
export const selectActivityStatusBySectionUserIdMap = createShallowRecordSelector(
  [selectEnabledUserIdBySectionUserIdMap, selectActivityStatus],
  (userIdBySectionUserId, userActivityStatus) => {
    const statusBySectionUserId: Record<string, SectionUserActivityStatus> = {};
    for (const [sectionUserId, userId] of Object.entries(userIdBySectionUserId)) {
      statusBySectionUserId[sectionUserId] = determineActivityStatus(
        userActivityStatus[userId] ?? null,
      );
    }
    return statusBySectionUserId;
  },
);

/** user_id → activity status for users with attendance data. */
export const selectActivityStatusByUserIdMap = createShallowRecordSelector(
  [selectActivityStatus],
  (sectionUserActivities) => {
    const statusByUserId: Record<string, SectionUserActivityStatus> = {};
    for (const [userId, activity] of Object.entries(sectionUserActivities)) {
      statusByUserId[userId] = determineActivityStatus(activity);
    }
    return statusByUserId;
  },
);

/** user_id → true for users currently following. */
export const selectIsFollowingByUserIdMap = createShallowRecordSelector(
  [selectActivityStatus],
  (sectionUserActivities) => {
    const isFollowingByUserId: Record<string, true> = {};
    for (const [userId, activity] of Object.entries(sectionUserActivities)) {
      if (activity.following === "1") {
        isFollowingByUserId[userId] = true;
      }
    }
    return isFollowingByUserId;
  },
);

export const selectActivityDataForUser = createShallowRecordSelector(
  [selectActivityStatus, (state, userId: string | null | undefined) => userId],
  (sectionUserActivities, userId) =>
    userId && userId in sectionUserActivities ? sectionUserActivities[userId] : null,
);

export const selectActivityStatusForUser = createAppSelector(
  [selectActivityDataForUser],
  (activityData) => determineActivityStatus(activityData),
);

export const selectFollowingStatusForUser = createAppSelector(
  [selectActivityDataForUser],
  (activityData) => activityData?.following === "1" || false,
);

export const selectActivePageIdForUser = createAppSelector(
  [selectActivityDataForUser],
  (activityData) => determineCurrentPageId(activityData),
);

export const selectActivityDataForCurrentUser = createAppSelector(
  [selectCurrentSectionId, selectActivityStatus],
  (sectionId, sectionUserActivities) => {
    return sectionId && sectionId in sectionUserActivities
      ? sectionUserActivities[sectionId] || null
      : null;
  },
);

export const selectActivePageIdForCurrentUser = createAppSelector(
  [selectActivityDataForCurrentUser],
  (activityData) => determineCurrentPageId(activityData),
);

export const selectUsersWithSectionActivityInCurrentSection = createAppSelector(
  [
    selectEnabledSectionUserIdsForCurrentSection,
    selectEnabledUserIdBySectionUserIdMap,
    userSelectors.selectEntities,
    selectActivityStatusBySectionUserIdMap,
  ],
  (
    sectionUserIds,
    userIdBySectionUserId,
    users,
    statusBySectionUserId,
  ): UserWithSectionActivity[] =>
    sectionUserIds.map((sectionUserId) => {
      const userId = userIdBySectionUserId[sectionUserId];
      const user = users[userId];
      return {
        ...user,
        section_user_id: sectionUserId,
        sectionActivityStatus: statusBySectionUserId[sectionUserId],
      };
    }),
);

export const selectStudentsWithSectionActivityInCurrentSection = createAppSelector(
  [selectUsersWithSectionActivityInCurrentSection, selectUserIdsWhoCanParticipateInSection],
  (users, studentUserIds) => {
    const studentUserIdSet = new Set(studentUserIds);
    return users.filter((user) => studentUserIdSet.has(user.id));
  },
);

/** True when instructors should be warned that some students are unassigned. */
export const selectHasStudentsNotInGroupByPageId = createAppSelector(
  [
    selectSectionUserIdsForPage,
    selectStudentSectionUserIdsInCurrentSection,
    selectActivityStatusBySectionUserIdMap,
  ],
  (groupedSectionUserIds, studentSectionUserIds, statusBySectionUserId) => {
    const groupedSectionUserIdSet = new Set(groupedSectionUserIds);
    const ungroupedSectionUserIds = studentSectionUserIds.filter(
      (sectionUserId) => !groupedSectionUserIdSet.has(sectionUserId),
    );
    const atLeastOneUngroupedOnlineStudent = ungroupedSectionUserIds.some((sectionUserId) => {
      const status = statusBySectionUserId[sectionUserId];
      return (
        status === SectionUserActivityStatus.ACTIVE || status === SectionUserActivityStatus.INACTIVE
      );
    });
    const atLeastOneStudentInGroup =
      ungroupedSectionUserIds.length !== studentSectionUserIds.length;

    return atLeastOneUngroupedOnlineStudent && atLeastOneStudentInGroup;
  },
);
